import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { landingAPI, productsAPI, uploadAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { renderPremiumTemplate, renderDarkLuxeTemplate, getDefaultSections, FONT_PAIRS, COLOR_PALETTES, NICHE_PHOTOS, PREMIUM_TEMPLATE_CATALOG } from '../lib/premiumTemplates';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, Loader2, Monitor, Tablet, Smartphone, Upload, ChevronDown, ChevronRight, Trash2, Plus, Image as ImageIcon } from 'lucide-react';

export default function PremiumLPEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const iframeRef = useRef(null);

  const [page, setPage] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [device, setDevice] = useState('desktop');
  const [activeSection, setActiveSection] = useState('hero');
  const [sections, setSections] = useState(null);
  const [style, setStyle] = useState({ primary: '#0F766E', accent: '#14B8A6', bg: '#FFFFFF', text: '#0F172A', fontHeading: 'Space Grotesk', fontBody: 'Inter' });

  const loadData = useCallback(async () => {
    try {
      const [pageRes, productsRes] = await Promise.all([landingAPI.get(id), productsAPI.list()]);
      const p = pageRes.data;
      setPage(p);
      setProducts(productsRes.data);
      // Load premium sections or init defaults
      const templateId = p.premium_template_id || 'business-clean';
      const saved = p.premium_sections;
      setSections(saved && Object.keys(saved).length > 0 ? saved : getDefaultSections(templateId));
      setStyle({
        primary: p.primary_color || '#0F766E',
        accent: p.accent_color || '#14B8A6',
        bg: p.background_color || '#FFFFFF',
        text: p.text_color || '#0F172A',
        fontHeading: p.premium_font_heading || 'Space Grotesk',
        fontBody: p.premium_font_body || 'Inter',
      });
    } catch { toast.error('Failed to load'); navigate('/app/landing-pages'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  // Update iframe preview
  useEffect(() => {
    if (!sections || !page) return;
    const templateId = page.premium_template_id || 'business-clean';
    const tmpl = PREMIUM_TEMPLATE_CATALOG.find(t => t.id === templateId);
    const html = tmpl?.renderer === 'darkLuxe'
      ? renderDarkLuxeTemplate(sections, style)
      : renderPremiumTemplate(templateId, sections, style);
    if (iframeRef.current) iframeRef.current.srcdoc = html;
  }, [sections, style, page]);

  const updateSection = (key, field, value) => {
    setSections(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const updateSectionItem = (sectionKey, itemIdx, field, value) => {
    setSections(prev => {
      const items = [...(prev[sectionKey]?.items || [])];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      return { ...prev, [sectionKey]: { ...prev[sectionKey], items } };
    });
  };

  const addSectionItem = (sectionKey, template) => {
    setSections(prev => {
      const items = [...(prev[sectionKey]?.items || []), template];
      return { ...prev, [sectionKey]: { ...prev[sectionKey], items } };
    });
  };

  const removeSectionItem = (sectionKey, idx) => {
    setSections(prev => {
      const items = (prev[sectionKey]?.items || []).filter((_, i) => i !== idx);
      return { ...prev, [sectionKey]: { ...prev[sectionKey], items } };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await landingAPI.update(id, {
        premium_sections: sections,
        primary_color: style.primary,
        accent_color: style.accent,
        background_color: style.bg,
        text_color: style.text,
        premium_font_heading: style.fontHeading,
        premium_font_body: style.fontBody,
      });
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (sectionKey, field) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await uploadAPI.thumbnail(fd);
        updateSection(sectionKey, field, res.data.thumbnail_url);
        toast.success('Image uploaded!');
      } catch { toast.error('Upload failed'); }
    };
    input.click();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" style={{ color: '#195259' }} /></div>;
  if (!page || !sections) return null;

  const SECTION_LIST = [
    { key: 'hero', label: 'Hero Section' },
    { key: 'features', label: 'Features' },
    { key: 'about', label: 'About' },
    { key: 'testimonials', label: 'Testimonials' },
    { key: 'cta', label: 'Call to Action' },
    { key: 'footer', label: 'Footer' },
  ];

  const deviceWidths = { desktop: '100%', tablet: '768px', mobile: '375px' };

  return (
    <div className="flex flex-col h-screen" data-testid="premium-editor">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b bg-white shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/landing-pages')} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="w-px h-5 bg-gray-200" />
        <span className="text-sm font-medium text-gray-700 truncate">{page.title || 'Untitled'}</span>
        <div className="flex-1" />
        {/* Device toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          {[{ id: 'desktop', icon: Monitor }, { id: 'tablet', icon: Tablet }, { id: 'mobile', icon: Smartphone }].map(d => (
            <button key={d.id} onClick={() => setDevice(d.id)}
              className={`p-1.5 rounded-md transition-colors ${device === d.id ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}>
              <d.icon className={`h-4 w-4 ${device === d.id ? 'text-[#195259]' : 'text-gray-400'}`} />
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-gray-200" />
        <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" style={{ backgroundColor: '#195259' }}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — section editor */}
        <div className="w-[340px] shrink-0 border-r bg-white overflow-y-auto">
          {/* Color palette quick pick */}
          <div className="p-4 border-b">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Color Theme</Label>
            <div className="flex flex-wrap gap-1.5">
              {COLOR_PALETTES.map(p => (
                <button key={p.id} onClick={() => setStyle(s => ({ ...s, primary: p.primary, accent: p.accent, bg: p.bg, text: p.text }))}
                  className={`w-7 h-7 rounded-lg border-2 transition-all ${style.primary === p.primary ? 'border-[#195259] scale-110' : 'border-transparent hover:scale-105'}`}
                  style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.accent})` }}
                  title={p.label} />
              ))}
            </div>
          </div>
          {/* Font pair pick */}
          <div className="p-4 border-b">
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Font Style</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {FONT_PAIRS.map(f => (
                <button key={f.id} onClick={() => setStyle(s => ({ ...s, fontHeading: f.heading, fontBody: f.body }))}
                  className={`px-2 py-1.5 rounded-lg text-[10px] font-medium transition-all border ${style.fontHeading === f.heading ? 'border-[#195259] bg-[#195259]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {/* Sections */}
          <div className="p-3 space-y-1">
            {SECTION_LIST.map(sec => {
              const isOpen = activeSection === sec.key;
              const sectionData = sections[sec.key] || {};
              return (
                <div key={sec.key} className="rounded-xl border bg-white overflow-hidden">
                  <button onClick={() => setActiveSection(isOpen ? null : sec.key)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-2">
                      {sec.key !== 'footer' && (
                        <Switch checked={sectionData.enabled !== false}
                          onCheckedChange={v => updateSection(sec.key, 'enabled', v)}
                          onClick={e => e.stopPropagation()} className="scale-75" />
                      )}
                      <span className="text-sm font-medium">{sec.label}</span>
                    </div>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </button>
                  {isOpen && (
                    <div className="px-3 pb-3 space-y-3 border-t pt-3">
                      {/* HERO */}
                      {sec.key === 'hero' && (<>
                        <div><Label className="text-xs">Headline</Label><Input value={sectionData.headline || ''} onChange={e => updateSection('hero', 'headline', e.target.value)} className="mt-1" /></div>
                        <div><Label className="text-xs">Subheadline</Label><Textarea value={sectionData.subheadline || ''} onChange={e => updateSection('hero', 'subheadline', e.target.value)} className="mt-1" rows={2} /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">Button Text</Label><Input value={sectionData.cta_text || ''} onChange={e => updateSection('hero', 'cta_text', e.target.value)} className="mt-1" /></div>
                          <div><Label className="text-xs">Button Link</Label><Input value={sectionData.cta_url || ''} onChange={e => updateSection('hero', 'cta_url', e.target.value)} className="mt-1" /></div>
                        </div>
                        <div>
                          <Label className="text-xs">Hero Image</Label>
                          <div className="mt-1 flex gap-2">
                            {sectionData.image && <img src={sectionData.image} alt="" className="h-16 w-24 rounded-lg object-cover border" />}
                            <button onClick={() => handleImageUpload('hero', 'image')} className="h-16 w-16 rounded-lg border-2 border-dashed flex items-center justify-center hover:border-[#195259]/50 transition-colors">
                              <Upload className="h-4 w-4 text-gray-400" />
                            </button>
                          </div>
                          <div className="flex gap-1 mt-2 flex-wrap">
                            {(NICHE_PHOTOS[page.premium_template_id?.split('-')[0]] || NICHE_PHOTOS.business).map((url, i) => (
                              <button key={i} onClick={() => updateSection('hero', 'image', url)} className={`h-10 w-14 rounded-md overflow-hidden border-2 transition-all ${sectionData.image === url ? 'border-[#FF9A00]' : 'border-transparent hover:border-gray-300'}`}>
                                <img src={url} alt="" className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </div>
                      </>)}
                      {/* FEATURES */}
                      {sec.key === 'features' && (<>
                        <div><Label className="text-xs">Section Title</Label><Input value={sectionData.title || ''} onChange={e => updateSection('features', 'title', e.target.value)} className="mt-1" /></div>
                        <div><Label className="text-xs">Subtitle</Label><Input value={sectionData.subtitle || ''} onChange={e => updateSection('features', 'subtitle', e.target.value)} className="mt-1" /></div>
                        <Label className="text-xs font-semibold">Items</Label>
                        {(sectionData.items || []).map((item, i) => (
                          <div key={i} className="flex gap-2 items-start p-2 rounded-lg bg-gray-50">
                            <div className="flex-1 space-y-1">
                              <Input value={item.title} onChange={e => updateSectionItem('features', i, 'title', e.target.value)} placeholder="Title" className="h-7 text-xs" />
                              <Input value={item.desc} onChange={e => updateSectionItem('features', i, 'desc', e.target.value)} placeholder="Description" className="h-7 text-xs" />
                            </div>
                            <button onClick={() => removeSectionItem('features', i)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="w-full gap-1" onClick={() => addSectionItem('features', { icon: 'star', title: 'New Feature', desc: 'Description here' })}>
                          <Plus className="h-3 w-3" /> Add Feature
                        </Button>
                      </>)}
                      {/* ABOUT */}
                      {sec.key === 'about' && (<>
                        <div><Label className="text-xs">Title</Label><Input value={sectionData.title || ''} onChange={e => updateSection('about', 'title', e.target.value)} className="mt-1" /></div>
                        <div><Label className="text-xs">Text</Label><Textarea value={sectionData.text || ''} onChange={e => updateSection('about', 'text', e.target.value)} className="mt-1" rows={3} /></div>
                        <div>
                          <Label className="text-xs">Image</Label>
                          <div className="mt-1 flex gap-2">
                            {sectionData.image && <img src={sectionData.image} alt="" className="h-14 w-20 rounded-lg object-cover border" />}
                            <button onClick={() => handleImageUpload('about', 'image')} className="h-14 w-14 rounded-lg border-2 border-dashed flex items-center justify-center hover:border-[#195259]/50"><Upload className="h-4 w-4 text-gray-400" /></button>
                          </div>
                        </div>
                      </>)}
                      {/* TESTIMONIALS */}
                      {sec.key === 'testimonials' && (<>
                        <div><Label className="text-xs">Section Title</Label><Input value={sectionData.title || ''} onChange={e => updateSection('testimonials', 'title', e.target.value)} className="mt-1" /></div>
                        {(sectionData.items || []).map((t, i) => (
                          <div key={i} className="p-2 rounded-lg bg-gray-50 space-y-1">
                            <div className="flex gap-2">
                              <Input value={t.name} onChange={e => updateSectionItem('testimonials', i, 'name', e.target.value)} placeholder="Name" className="h-7 text-xs" />
                              <Input value={t.role || ''} onChange={e => updateSectionItem('testimonials', i, 'role', e.target.value)} placeholder="Role" className="h-7 text-xs" />
                              <button onClick={() => removeSectionItem('testimonials', i)} className="p-1 text-gray-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                            </div>
                            <Textarea value={t.text} onChange={e => updateSectionItem('testimonials', i, 'text', e.target.value)} placeholder="Testimonial text" className="text-xs" rows={2} />
                          </div>
                        ))}
                        <Button variant="outline" size="sm" className="w-full gap-1" onClick={() => addSectionItem('testimonials', { name: 'Customer', role: '', text: 'Great product!', rating: 5 })}>
                          <Plus className="h-3 w-3" /> Add Testimonial
                        </Button>
                      </>)}
                      {/* CTA */}
                      {sec.key === 'cta' && (<>
                        <div><Label className="text-xs">Title</Label><Input value={sectionData.title || ''} onChange={e => updateSection('cta', 'title', e.target.value)} className="mt-1" /></div>
                        <div><Label className="text-xs">Subtitle</Label><Input value={sectionData.subtitle || ''} onChange={e => updateSection('cta', 'subtitle', e.target.value)} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="text-xs">Button Text</Label><Input value={sectionData.button_text || ''} onChange={e => updateSection('cta', 'button_text', e.target.value)} className="mt-1" /></div>
                          <div><Label className="text-xs">Button Link</Label><Input value={sectionData.button_url || ''} onChange={e => updateSection('cta', 'button_url', e.target.value)} className="mt-1" /></div>
                        </div>
                      </>)}
                      {/* FOOTER */}
                      {sec.key === 'footer' && (
                        <div><Label className="text-xs">Footer Text</Label><Input value={sectionData.text || ''} onChange={e => updateSection('footer', 'text', e.target.value)} className="mt-1" placeholder="© 2026 Your Brand" /></div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right — Live preview */}
        <div className="flex-1 bg-[#f0f0f0] flex items-start justify-center overflow-auto p-4">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300" style={{ width: deviceWidths[device], maxWidth: '100%' }}>
            <iframe ref={iframeRef} title="Preview" className="w-full border-0" style={{ height: 'calc(100vh - 80px)', minHeight: '600px' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
